// The small previews of the LIVERY section (monetization.md §3.2, R1252).
//
// One cell shows one Bombard of the default ship icon set in the viewer's
// faction ink, at a heading of -30 degrees, wearing one
// option. It is drawn by `bakeLivery`, the bake the atlas uses, and then
// tinted, so the card shows what the map shows: a dark step lets the ground
// of the cell show through the hull, and the light step lifts the ink.
//
// A preview is a canvas turned into an image URL once per option, faction
// and scale, so the card markup stays stable when the pane is rewritten.
// `scale` bakes the picture larger than the cell: the tiles of FLEET
// LIVERY and its companion column draw it wider than 64 px (R2186).

import { LIVERY_INK, type LiveryCode } from '../../../gen/presentation';
import { COMBAT_ICONS, Canvas2DMarks, lift, type HullPath } from '../../../render/marks';
import { bakeLivery, hullSpan, liveryOfCode, type LiveryMaster } from '../../../render/livery';
import { ShipClass } from '../../../gen/proto/aquaphobia';
import { FLEET_HOLLOW_STROKE_PX } from '../../../gen/presentation';
import { hex } from '../../../world';
import { MAX_DPR, deviceRatio } from '../../../render/dpr';

/** The cell, in CSS px, and how many canvas px a hull px takes. */
export const PREVIEW_W = 64;
export const PREVIEW_H = 44;
const HULL_PX = 1.6;
/** The heading of the hull in the cell. */
const HEAD = (-30 * Math.PI) / 180;

const BOMBARD: HullPath = COMBAT_ICONS.find(([c]) => c === ShipClass.SHIP_CLASS_BOMBARD)![1];

const cache = new Map<string, string>();

/** One tinted layer: the white bake of `master`, dyed `ink`. */
function layer(
  w: number, h: number, k: number, code: LiveryCode | '', master: LiveryMaster, ink: number,
): HTMLCanvasElement | null {
  const cv = document.createElement('canvas');
  cv.width = w; cv.height = h;
  const c = cv.getContext('2d');
  if (!c) return null;
  c.translate(w / 2, h / 2);
  c.rotate(HEAD);
  c.scale(k, k);
  c.translate(-2.5, 0);
  const row = code === '' ? null : liveryOfCode(code);
  if (row) {
    bakeLivery(c, {
      path: BOMBARD, hull: { support: false, span: hullSpan(BOMBARD) }, row, master,
      cockpitDot: false, hollowStrokePx: FLEET_HOLLOW_STROKE_PX,
    });
  } else {
    bakePlain(c);
  }
  c.setTransform(1, 0, 0, 1, 0, 0);
  c.globalCompositeOperation = 'source-in';
  c.fillStyle = hex(ink);
  c.fillRect(0, 0, w, h);
  return cv;
}

/** NONE: the plain white master, the fill of `atlas.ts hullSpec`. */
function bakePlain(c: CanvasRenderingContext2D): void {
  c.beginPath();
  BOMBARD(new Canvas2DMarks(c));
  c.fillStyle = '#ffffff';
  c.fill();
}

/**
 * The image URL of one preview, '' where the document has no 2D canvas.
 * `code` '' is NONE; `faction` is the faction ink, 0xRRGGBB; `scale` is
 * how many cells of `PREVIEW_W` x `PREVIEW_H` the picture bakes at.
 */
export function liveryPreviewUrl(code: LiveryCode | '', faction: number, scale = 1): string {
  const key = `${code}:${faction}:${scale}`;
  const hit = cache.get(key);
  if (hit !== undefined) return hit;
  // The shared cap of every raster (R2249).
  const dpr = Math.max(1, deviceRatio(MAX_DPR)) * Math.max(1, scale);
  const w = Math.round(PREVIEW_W * dpr), h = Math.round(PREVIEW_H * dpr), k = HULL_PX * dpr;
  const row = code === '' ? null : liveryOfCode(code);
  const out = document.createElement('canvas');
  out.width = w; out.height = h;
  const c = out.getContext('2d');
  const hull = layer(w, h, k, code, 'filled', faction);
  if (!c || !hull) {
    cache.set(key, '');
    return '';
  }
  c.drawImage(hull, 0, 0);
  if (row && row.step === 'light') {
    const mask = layer(w, h, k, code, 'mask', lift(faction, LIVERY_INK.lightLift));
    if (mask) c.drawImage(mask, 0, 0);
  }
  const url = out.toDataURL('image/png');
  cache.set(key, url);
  return url;
}
